import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { toast } from 'react-toastify';
import Select from 'react-select';
import StockItemSelectionModal from '../../components/StockItemSelectionModal';
import { FaPlus, FaEdit, FaEye, FaSearch, FaTimes, FaFilter, FaShoppingCart,
  FaClock, FaCheckCircle, FaBan, FaBox, FaCalendarAlt, FaUser, FaTruck, FaBoxes } from 'react-icons/fa';

const initialFormState = (userId) => ({
  supplier: null,
  items: [{
    stockItemId: '',
    itemName: '',
    orderedQuantity: 0,
    unitPrice: 0,
    totalAmount: 0,
    unitOfMeasure: '',
    itemDetails: null
  }],
  expectedDeliveryDate: '',
  remarks: '',
  createdBy: userId
});

const StockItemsOrder = () => {
  const { user } = useContext(AuthContext);
  const userId = user?.user?._id;
  const companyId = user?.user?.company?._id || user?.user?.company;

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // State
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState(null);
  const [dateRangeFilter, setDateRangeFilter] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [form, setForm] = useState(initialFormState(userId));
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, hasNext: false, hasPrev: false });
  
  // Modals
  const [showItemModal, setShowItemModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);

  // ── Data Loading ──
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers/all', { params: { userCompanyId: companyId } });
      const data = response.data?.data || response.data || [];
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  }, [companyId]);

  const fetchOrders = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 10, company: companyId });
      if (statusFilter) params.append('status', statusFilter.value);
      if (selectedSupplierFilter) params.append('supplier', selectedSupplierFilter.value);
      if (dateRangeFilter.startDate) params.append('startDate', dateRangeFilter.startDate);
      if (dateRangeFilter.endDate) params.append('endDate', dateRangeFilter.endDate);
      
      const response = await api.get(`/stock-items-orders?${params}`);
      const { orders: ordersList, pagination: pag } = response.data?.data || {};
      setOrders(ordersList || []);
      setFilteredOrders(ordersList || []);
      setPagination(pag || { currentPage: 1, totalPages: 1, totalItems: 0 });
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [companyId, statusFilter, selectedSupplierFilter, dateRangeFilter]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Client-side search filtering
  useEffect(() => {
    if (!orderSearchTerm.trim()) {
      setFilteredOrders(orders);
    } else {
      const term = orderSearchTerm.toLowerCase();
      setFilteredOrders(orders.filter(o =>
        o.orderId?.toLowerCase().includes(term) ||
        o.supplier?.name?.toLowerCase().includes(term) ||
        o.items?.some(i => i.stockItemId?.name?.toLowerCase().includes(term))
      ));
    }
  }, [orderSearchTerm, orders]);

  // ── Supplier Modal ──
  useEffect(() => {
    if (showSupplierModal) {
      if (!supplierSearch.trim()) {
        setFilteredSuppliers(suppliers);
      } else {
        const term = supplierSearch.toLowerCase();
        setFilteredSuppliers(suppliers.filter(s =>
          s.name?.toLowerCase().includes(term) ||
          s.contactDetails?.toLowerCase().includes(term)
        ));
      }
    }
  }, [supplierSearch, suppliers, showSupplierModal]);

  const handleSupplierSelect = (supplier) => {
    setForm(prev => ({ ...prev, supplier: { value: supplier._id, label: supplier.name, data: supplier } }));
    setShowSupplierModal(false);
    setSupplierSearch('');
  };

  // ── Item Selection ──
  const openItemModal = (index) => {
    setCurrentItemIndex(index);
    setShowItemModal(true);
  };

  const handleItemSelect = (item) => {
    if (currentItemIndex === null) return;
    setForm(prev => {
      const items = [...prev.items];
      items[currentItemIndex] = {
        ...items[currentItemIndex],
        stockItemId: item._id,
        itemName: item.name,
        unitOfMeasure: item.unitOfMeasure || '',
        itemDetails: item,
        unitPrice: items[currentItemIndex].unitPrice || 0,
        orderedQuantity: items[currentItemIndex].orderedQuantity || 0
      };
      items[currentItemIndex].totalAmount = items[currentItemIndex].orderedQuantity * items[currentItemIndex].unitPrice;
      return { ...prev, items };
    });
    setShowItemModal(false);
    setCurrentItemIndex(null);
  };

  // ── Form helpers ──
  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { stockItemId: '', itemName: '', orderedQuantity: 0, unitPrice: 0, totalAmount: 0, unitOfMeasure: '', itemDetails: null }]
    }));
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return toast.warning('At least one item is required');
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItemField = (index, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'orderedQuantity' || field === 'unitPrice') {
        items[index].totalAmount = (parseFloat(items[index].orderedQuantity) || 0) * (parseFloat(items[index].unitPrice) || 0);
      }
      return { ...prev, items };
    });
  };

  const calculateTotalOrderValue = (items) => items.reduce((sum, item) => sum + (item.totalAmount || 0), 0);

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplier) return toast.error('Please select a supplier');
    const validItems = form.items.filter(i => i.stockItemId && i.orderedQuantity > 0);
    if (validItems.length === 0) return toast.error('Please add at least one item with quantity > 0');

    try {
      setSubmitting(true);
      const payload = {
        supplier: form.supplier.value,
        company: companyId,
        items: validItems.map(i => ({
          stockItemId: i.stockItemId,
          orderedQuantity: parseFloat(i.orderedQuantity),
          unitPrice: parseFloat(i.unitPrice) || 0,
          totalAmount: i.totalAmount || 0
        })),
        expectedDeliveryDate: form.expectedDeliveryDate || undefined,
        remarks: form.remarks || undefined,
        createdBy: userId
      };

      if (isEditing && orderId) {
        await api.put(`/stock-items-orders/${orderId}`, payload);
        toast.success('Order updated successfully');
      } else {
        await api.post('/stock-items-orders', payload);
        toast.success('Order created successfully');
      }
      resetForm();
      fetchOrders();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Failed to save order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (order) => {
    try {
      const response = await api.get(`/stock-items-orders/${order._id}`);
      const orderData = response.data?.data;
      if (!orderData) return toast.error('Failed to load order details');

      const supplier = suppliers.find(s => s._id === (orderData.supplier?._id || orderData.supplier));
      setForm({
        supplier: supplier ? { value: supplier._id, label: supplier.name, data: supplier } : null,
        items: orderData.items.map(i => ({
          stockItemId: i.stockItemId?._id || i.stockItemId,
          itemName: i.stockItemId?.name || i.itemName || '',
          orderedQuantity: i.orderedQuantity || 0,
          unitPrice: i.unitPrice || 0,
          totalAmount: i.totalAmount || 0,
          unitOfMeasure: i.stockItemId?.unitOfMeasure || '',
          itemDetails: i.stockItemId
        })),
        expectedDeliveryDate: orderData.expectedDeliveryDate ? new Date(orderData.expectedDeliveryDate).toISOString().split('T')[0] : '',
        remarks: orderData.remarks || '',
        createdBy: userId
      });
      setIsEditing(true);
      setOrderId(order._id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Edit error:', error);
      toast.error('Failed to load order for editing');
    }
  };

  const handleView = async (order) => {
    try {
      const response = await api.get(`/stock-items-orders/${order._id}`);
      setSelectedOrder(response.data?.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const handleCancelOrder = async (order) => {
    const result = await Swal.fire({
      title: 'Cancel Order',
      text: `Are you sure you want to cancel order ${order.orderId}?`,
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Reason for cancellation',
      inputPlaceholder: 'Enter reason...',
      showCancelButton: true,
      confirmButtonText: 'Yes, Cancel',
      confirmButtonColor: '#ef4444',
      inputValidator: (value) => { if (!value) return 'Please provide a reason'; }
    });

    if (result.isConfirmed) {
      try {
        await api.put(`/stock-items-orders/${order._id}/status`, {
          status: 'cancelled',
          cancelReason: result.value,
          cancelledBy: userId
        });
        toast.success('Order cancelled successfully');
        fetchOrders();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to cancel order');
      }
    }
  };

  const resetForm = () => {
    setForm(initialFormState(userId));
    setIsEditing(false);
    setOrderId(null);
  };

  // ── Status Badge ──
  const getStatusBadge = (status) => {
    const config = {
      'pending': { bg: 'bg-yellow-100 text-yellow-800', icon: <FaClock className="mr-1" />, label: 'Pending' },
      'partially-dispatched': { bg: 'bg-blue-100 text-blue-800', icon: <FaTruck className="mr-1" />, label: 'Partially Dispatched' },
      'fully-dispatched': { bg: 'bg-indigo-100 text-indigo-800', icon: <FaTruck className="mr-1" />, label: 'Fully Dispatched' },
      'partial': { bg: 'bg-orange-100 text-orange-800', icon: <FaBox className="mr-1" />, label: 'Partial' },
      'completed': { bg: 'bg-green-100 text-green-800', icon: <FaCheckCircle className="mr-1" />, label: 'Completed' },
      'cancelled': { bg: 'bg-red-100 text-red-800', icon: <FaBan className="mr-1" />, label: 'Cancelled' }
    };
    const c = config[status] || { bg: 'bg-gray-100 text-gray-800', label: status };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg}`}>{c.icon}{c.label}</span>;
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'partially-dispatched', label: 'Partially Dispatched' },
    { value: 'fully-dispatched', label: 'Fully Dispatched' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const inputBg = darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className={`min-h-screen p-4 md:p-6 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-3 rounded-xl"><FaShoppingCart className="text-white w-8 h-8" /></div>
          <div>
            <h1 className="text-2xl font-bold text-white">Stock Items Order</h1>
            <p className="text-white/80 text-sm">Create and manage purchase orders for stock items</p>
          </div>
        </div>
      </div>

      {/* Order Form */}
      <div className={`${cardBg} border rounded-2xl shadow-lg mb-6 overflow-hidden`}>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaPlus className="w-4 h-4" />{isEditing ? 'Edit Order' : 'New Order'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Supplier + Date Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Supplier *</label>
              <button type="button" onClick={() => setShowSupplierModal(true)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 border-dashed transition-all ${
                  form.supplier
                    ? `${darkMode ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-300 bg-indigo-50'}`
                    : `${darkMode ? 'border-gray-600 hover:border-indigo-500' : 'border-gray-300 hover:border-indigo-400'}`
                }`}>
                {form.supplier ? (
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-indigo-500" />
                    <span className={`font-medium ${textPrimary}`}>{form.supplier.label}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FaSearch className={textSecondary} />
                    <span className={textSecondary}>Click to select supplier</span>
                  </div>
                )}
              </button>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Expected Delivery Date</label>
              <input type="date" value={form.expectedDeliveryDate}
                onChange={(e) => setForm(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`} />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={`text-sm font-medium ${textSecondary}`}>Order Items</label>
              <button type="button" onClick={addItem}
                className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors">
                <FaPlus className="w-3 h-3" /> Add Item
              </button>
            </div>
            <div className="space-y-3">
              {form.items.map((item, index) => (
                <div key={index} className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-xl p-4 border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-200 dark:border-gray-600">
                    <span className={`text-sm font-semibold ${textPrimary}`}>Item #{index + 1}</span>
                    {form.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)}
                        className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        <FaTimes className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {/* Item selector */}
                  <button type="button" onClick={() => openItemModal(index)}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 border-dashed mb-3 transition-all ${
                      item.stockItemId
                        ? `${darkMode ? 'border-green-500 bg-green-900/20' : 'border-green-300 bg-green-50'}`
                        : `${darkMode ? 'border-gray-600 hover:border-indigo-500' : 'border-gray-300 hover:border-indigo-400'}`
                    }`}>
                    {item.stockItemId ? (
                      <div className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500" />
                        <span className={`font-medium ${textPrimary}`}>{item.itemName}</span>
                        {item.unitOfMeasure && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">{item.unitOfMeasure}</span>}
                        <span className={`text-xs ${textSecondary}`}>Click to change</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <FaSearch className={textSecondary} />
                        <span className={textSecondary}>Click to search and select a stock item</span>
                      </div>
                    )}
                  </button>
                  {/* Quantity, Price, Total */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={`block text-xs mb-1 ${textSecondary}`}>Quantity {item.unitOfMeasure ? `(${item.unitOfMeasure})` : ''}</label>
                      <input type="number" min="0" value={item.orderedQuantity || ''}
                        onChange={(e) => updateItemField(index, 'orderedQuantity', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border ${inputBg} text-sm focus:ring-2 focus:ring-indigo-500`}
                        placeholder="0" />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${textSecondary}`}>Unit Price (₹)</label>
                      <input type="number" min="0" step="0.01" value={item.unitPrice || ''}
                        onChange={(e) => updateItemField(index, 'unitPrice', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border ${inputBg} text-sm focus:ring-2 focus:ring-indigo-500`}
                        placeholder="0.00" />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${textSecondary}`}>Total</label>
                      <div className={`px-3 py-2 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-gray-100'} text-sm font-medium ${textPrimary}`}>
                        {formatCurrency(item.totalAmount)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total Order Value */}
          <div className={`flex items-center justify-between px-4 py-3 rounded-xl ${darkMode ? 'bg-indigo-900/30 border-indigo-700' : 'bg-indigo-50 border-indigo-200'} border`}>
            <span className={`font-medium ${textPrimary}`}>Total Order Value</span>
            <span className="text-xl font-bold text-indigo-600">{formatCurrency(calculateTotalOrderValue(form.items))}</span>
          </div>

          {/* Remarks */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Remarks</label>
            <textarea value={form.remarks} onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
              className={`w-full px-4 py-3 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`}
              rows={2} placeholder="Optional notes..." />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button type="submit" disabled={submitting}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50">
              {submitting ? 'Saving...' : isEditing ? 'Update Order' : 'Create Order'}
            </button>
            <button type="button" onClick={resetForm}
              className={`px-6 py-3 rounded-xl border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} transition-colors`}>
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Orders List */}
      <div className={`${cardBg} border rounded-2xl shadow-lg overflow-hidden`}>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaBoxes className="w-4 h-4" /> Orders List
          </h2>
        </div>
        <div className="p-4">
          {/* Search + Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px] relative">
              <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
              <input type="text" value={orderSearchTerm} onChange={(e) => setOrderSearchTerm(e.target.value)}
                placeholder="Search orders..."
                className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`} />
              {orderSearchTerm && (
                <button onClick={() => setOrderSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <FaTimes />
                </button>
              )}
            </div>
            <div className="w-48">
              <Select options={statusOptions} value={statusFilter} onChange={setStatusFilter} isClearable
                placeholder="Status" className="text-sm"
                styles={{ control: (base) => ({ ...base, borderRadius: '0.75rem', minHeight: '42px' }) }} />
            </div>
            <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`px-4 py-2 rounded-xl border flex items-center gap-2 text-sm transition-colors ${
                showAdvancedFilters ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : `${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}`}>
              <FaFilter /> Filters
            </button>
          </div>

          {showAdvancedFilters && (
            <div className={`p-4 mb-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} grid grid-cols-1 md:grid-cols-3 gap-3`}>
              <div>
                <label className={`text-xs font-medium ${textSecondary}`}>Supplier</label>
                <Select options={suppliers.map(s => ({ value: s._id, label: s.name }))} value={selectedSupplierFilter}
                  onChange={setSelectedSupplierFilter} isClearable placeholder="All"
                  className="text-sm mt-1" />
              </div>
              <div>
                <label className={`text-xs font-medium ${textSecondary}`}>Start Date</label>
                <input type="date" value={dateRangeFilter.startDate}
                  onChange={(e) => setDateRangeFilter(prev => ({ ...prev, startDate: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${inputBg}`} />
              </div>
              <div>
                <label className={`text-xs font-medium ${textSecondary}`}>End Date</label>
                <input type="date" value={dateRangeFilter.endDate}
                  onChange={(e) => setDateRangeFilter(prev => ({ ...prev, endDate: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${inputBg}`} />
              </div>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div><p className={textSecondary}>Loading orders...</p></div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <FaBoxes className={`w-8 h-8 ${textSecondary}`} />
              </div>
              <h3 className={`text-lg font-medium mb-1 ${textPrimary}`}>No Orders Found</h3>
              <p className={textSecondary}>Create your first stock items order above</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                      <th className="px-4 py-3 text-left text-sm font-medium">Order Details</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Items</th>
                      <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredOrders.map((order) => (
                      <tr key={order._id} className={`${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`}>
                        <td className="px-4 py-3">
                          <div className={`font-medium ${textPrimary}`}>{order.orderId}</div>
                          <div className={`text-xs ${textSecondary}`}>
                            <FaCalendarAlt className="inline mr-1" />{new Date(order.createdAt).toLocaleDateString('en-IN')}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`text-sm ${textPrimary}`}>{order.supplier?.name || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${darkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                            {order.items?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-semibold ${textPrimary}`}>{formatCurrency(order.totalOrderValue)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(order.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleView(order)} title="View"
                              className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                              <FaEye className="w-4 h-4" />
                            </button>
                            {['pending', 'partially-dispatched'].includes(order.status) && (
                              <button onClick={() => handleEdit(order)} title="Edit"
                                className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
                                <FaEdit className="w-4 h-4" />
                              </button>
                            )}
                            {order.status === 'pending' && (
                              <button onClick={() => handleCancelOrder(order)} title="Cancel"
                                className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                <FaBan className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 mt-2">
                <span className={`text-sm ${textSecondary}`}>
                  Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} orders)
                </span>
                <div className="flex gap-2">
                  <button onClick={() => fetchOrders(pagination.currentPage - 1)} disabled={!pagination.hasPrev}
                    className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-gray-600' : 'border-gray-300'} disabled:opacity-40`}>Previous</button>
                  <button onClick={() => fetchOrders(pagination.currentPage + 1)} disabled={!pagination.hasNext}
                    className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-gray-600' : 'border-gray-300'} disabled:opacity-40`}>Next</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Supplier Selection Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowSupplierModal(false)}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${textPrimary}`}>Select Supplier</h3>
              <button onClick={() => setShowSupplierModal(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><FaTimes /></button>
            </div>
            <div className="p-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={supplierSearch} onChange={(e) => setSupplierSearch(e.target.value)}
                  placeholder="Search suppliers..." autoFocus
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${inputBg}`} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {filteredSuppliers.length === 0 ? (
                <p className={`text-center py-8 ${textSecondary}`}>No suppliers found</p>
              ) : filteredSuppliers.map(supplier => (
                <button key={supplier._id} onClick={() => handleSupplierSelect(supplier)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    form.supplier?.value === supplier._id
                      ? `${darkMode ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-400 bg-indigo-50'}`
                      : `${darkMode ? 'border-gray-700 hover:border-indigo-500' : 'border-gray-200 hover:border-indigo-300'}`
                  }`}>
                  <div className={`font-medium ${textPrimary}`}>{supplier.name}</div>
                  {supplier.contactDetails && <div className={`text-xs mt-0.5 ${textSecondary}`}>{supplier.contactDetails}</div>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stock Item Selection Modal */}
      <StockItemSelectionModal
        isOpen={showItemModal}
        onClose={() => { setShowItemModal(false); setCurrentItemIndex(null); }}
        onSelect={handleItemSelect}
        darkMode={darkMode}
        userId={userId}
      />

      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowViewModal(false)}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedOrder.orderId}</h3>
                <p className="text-white/70 text-sm">Created: {new Date(selectedOrder.createdAt).toLocaleDateString('en-IN')}</p>
              </div>
              <button onClick={() => setShowViewModal(false)} className="p-2 rounded-lg text-white hover:bg-white/20"><FaTimes /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs ${textSecondary}`}>Supplier</label>
                  <p className={`font-medium ${textPrimary}`}>{selectedOrder.supplier?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Status</label>
                  <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Expected Delivery</label>
                  <p className={`${textPrimary}`}>{selectedOrder.expectedDeliveryDate ? new Date(selectedOrder.expectedDeliveryDate).toLocaleDateString('en-IN') : 'Not set'}</p>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Total Value</label>
                  <p className="font-bold text-indigo-600">{formatCurrency(selectedOrder.totalOrderValue)}</p>
                </div>
              </div>

              <div>
                <h4 className={`text-sm font-semibold mb-2 ${textPrimary}`}>Items</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <th className={`px-3 py-2 text-left ${textSecondary}`}>Item</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Ordered</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Dispatched</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Received</th>
                        <th className={`px-3 py-2 text-right ${textSecondary}`}>Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className={`px-3 py-2 ${textPrimary}`}>{item.stockItemId?.name || item.itemName || 'N/A'}</td>
                          <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.orderedQuantity}</td>
                          <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.dispatchedQuantity || 0}</td>
                          <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.receivedQuantity || 0}</td>
                          <td className={`px-3 py-2 text-right ${textPrimary}`}>{formatCurrency(item.totalAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedOrder.remarks && (
                <div>
                  <label className={`text-xs ${textSecondary}`}>Remarks</label>
                  <p className={`${textPrimary} text-sm mt-1`}>{selectedOrder.remarks}</p>
                </div>
              )}
              {selectedOrder.status === 'cancelled' && (
                <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">Cancellation Reason:</p>
                  <p className="text-sm text-red-600 dark:text-red-300">{selectedOrder.cancelReason || 'No reason provided'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockItemsOrder;
